<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { estimateSegment } from '../../api/segments';
import CommsChoiceGroup from './CommsChoiceGroup.vue';
import XlButton from '../xl/XlButton.vue';
import XlDropdown from '../xl/XlDropdown.vue';
import XlField from '../xl/XlField.vue';
import XlDateTimeField from '../xl/XlDateTimeField.vue';
import { usePolicyLinesStore } from '../../stores/policyLines';
import {
  CONDITION_FIELDS,
  OPS_BY_FIELD,
  createCondition,
  defaultOpForField,
  defaultValueForField,
  isConditionComplete,
  policyTypeList,
  policyTypeOptions,
  takesDays,
} from './segmentRuleHelpers';

const props = defineProps({
  modelValue: { type: Object, required: true },
});

const emit = defineEmits(['update:modelValue', 'estimate-changed', 'update:valid']);

const ESTIMATE_DEBOUNCE_MS = 400;

const DEFINITION_TYPE_OPTIONS = [
  { value: 'policy_type', label: 'Policy type' },
  { value: 'custom_rule', label: 'Custom rule' },
];

// The names on these options come from the hub's policy lines (XN-4535), which
// arrive asynchronously — so nothing below may treat "not in the list yet" as
// "not a real type".
const policyLines = usePolicyLinesStore();

onMounted(() => policyLines.load());

const definitionType = ref(props.modelValue?.definition_type === 'custom_rule' ? 'custom_rule' : 'policy_type');
// Several types, not one: "Auto or Home renewals" is a segment somebody
// actually wants, and it took two identical segments to express before.
// A rule saved as a bare string still reads back here.
//
// Saved values are NOT filtered against the offered list. They used to be, back
// when that list was a hard-coded constant that was always present; against a
// list that loads later — or one an archived line has left — filtering silently
// empties a saved segment and changes who it reaches. An unrecognised type is
// kept and labelled by its own key instead.
const policyTypes = ref(
  definitionType.value === 'policy_type' ? policyTypeList(props.modelValue?.policy_type) : []
);
const conditions = ref(
  definitionType.value === 'custom_rule' && Array.isArray(props.modelValue?.conditions)
    ? props.modelValue.conditions.map((c) => ({ field: c.field, op: c.op, value: c.value }))
    : []
);

const offeredPolicyTypes = computed(() => policyTypeOptions(policyTypes.value));


// Each mode keeps its own choices, as the prototype does, so switching back
// restores them; currentRule emits only the active mode's.
watch(definitionType, (mode) => {
  if (mode === 'custom_rule' && !conditions.value.length) conditions.value = [createCondition('policy_type')];
});

const addCondition = () => {
  conditions.value = [...conditions.value, createCondition('policy_type')];
};

const removeCondition = (index) => {
  conditions.value = conditions.value.filter((_, i) => i !== index);
};

const valueKind = (c) => {
  if (c.field === 'policy_type') return c.op === 'within' ? 'policy_types' : 'policy_type';
  if (c.field === 'state') return 'state';
  return takesDays(c.op) ? 'days' : 'date';
};

// The operator and value carry over to the new field wherever they still fit it.
const onFieldChange = (index, field) => {
  conditions.value = conditions.value.map((c, i) => {
    if (i !== index) return c;
    const op = OPS_BY_FIELD[field].some((o) => o.value === c.op) ? c.op : defaultOpForField(field);
    const next = { field, op, value: c.value };
    return valueKind(next) === valueKind(c) ? next : { ...next, value: defaultValueForField(field, op) };
  });
};

// One policy type becomes a list of one under "within", and the first of the
// list when going back.
const carriedValue = (c, op) => {
  if (c.field !== 'policy_type') return c.value;
  if (op === 'within') return policyTypeList(c.value);
  return Array.isArray(c.value) ? c.value[0] : c.value;
};

// "is" to "is not", or "before" to "after", keeps the value already chosen.
const onOpChange = (index, op) => {
  conditions.value = conditions.value.map((c, i) => {
    if (i !== index) return c;
    const kept = { ...c, op, value: carriedValue(c, op) };
    return isConditionComplete(kept) ? kept : { ...kept, value: defaultValueForField(c.field, op) };
  });
};

// isConditionComplete requires a real Number for this field — matches Vue's
// own v-model.number coercion (unparseable input passes through as-is).
const looseToNumber = (value) => {
  const n = Number.parseFloat(value);
  return Number.isNaN(n) ? value : n;
};

const onConditionNumberInput = (index, rawValue) => {
  const value = looseToNumber(rawValue);
  conditions.value = conditions.value.map((c, i) => (i === index ? { ...c, value } : c));
};

const currentRule = computed(() => {
  if (definitionType.value === 'policy_type') {
    return { definition_type: 'policy_type', policy_type: policyTypes.value };
  }
  return {
    definition_type: 'custom_rule',
    conditions: conditions.value.map((c) => ({ field: c.field, op: c.op, value: c.value })),
  };
});

const validationError = computed(() => {
  if (definitionType.value === 'policy_type') {
    return policyTypes.value.length === 0 ? 'Pick at least one policy type.' : '';
  }
  if (definitionType.value !== 'custom_rule') return '';
  if (conditions.value.length === 0) {
    return 'Add at least one condition — a custom rule with none would silently match every active policy.';
  }
  const incomplete = conditions.value.find((c) => !isConditionComplete(c));
  if (incomplete) {
    if (takesDays(incomplete.op)) {
      return 'Enter a positive whole number of days.';
    }
    return 'Complete every condition before saving.';
  }
  return '';
});

const isValid = computed(() => validationError.value === '');

// With no type picked, the audience panel beside the form already says so.
const shownError = computed(() => (definitionType.value === 'custom_rule' ? validationError.value : ''));

const estimate = ref({ policies: null, policyholders: null, mine: null, sample: [] });
const estimateLoading = ref(false);
const estimateError = ref('');

let debounceHandle = null;
let requestSeq = 0;

const fetchEstimate = async (rule) => {
  const seq = ++requestSeq;
  estimateLoading.value = true;
  estimateError.value = '';
  try {
    const data = await estimateSegment(rule);
    if (seq !== requestSeq) return; // superseded by a later edit
    estimate.value = {
      policies: data.policies,
      policyholders: data.policyholders,
      mine: data.mine ?? null,
      sample: data.sample || [],
    };
    emit('estimate-changed', estimate.value);
  } catch {
    if (seq !== requestSeq) return;
    estimateError.value = 'Could not estimate this rule right now.';
  } finally {
    if (seq === requestSeq) estimateLoading.value = false;
  }
};

const scheduleEstimate = (rule) => {
  if (debounceHandle) clearTimeout(debounceHandle);
  debounceHandle = setTimeout(() => {
    debounceHandle = null;
    fetchEstimate(rule);
  }, ESTIMATE_DEBOUNCE_MS);
};

watch(
  currentRule,
  (rule) => {
    emit('update:modelValue', rule);

    if (!isValid.value) {
      // An invalid rule (e.g. zero conditions) must never reach the estimate
      // endpoint — it has no defined meaning there, and any in-flight
      // request for a prior, valid rule is now stale.
      if (debounceHandle) {
        clearTimeout(debounceHandle);
        debounceHandle = null;
      }
      requestSeq += 1;
      estimate.value = { policies: null, policyholders: null, mine: null, sample: [] };
      emit('estimate-changed', estimate.value);
      return;
    }

    scheduleEstimate(rule);
  },
  { immediate: true }
);

watch(isValid, (valid) => emit('update:valid', valid), { immediate: true });

onBeforeUnmount(() => {
  if (debounceHandle) clearTimeout(debounceHandle);
});
</script>


<template>
  <div class="segment-rule-builder" :data-valid="isValid ? 'true' : 'false'">
    <CommsChoiceGroup
      class="segment-rule-builder__fld"
      testid="segment-definition-type"
      label="Define by"
      equal
      :options="DEFINITION_TYPE_OPTIONS"
      v-model="definitionType"
    />

    <CommsChoiceGroup
      v-if="definitionType === 'policy_type'"
      class="segment-rule-builder__fld"
      testid="policy-type"
      label="Policy type"
      multiple
      size="lg"
      :options="offeredPolicyTypes"
      v-model="policyTypes"
    />

    <div v-else class="segment-rule-builder__fld">
      <span class="segment-rule-builder__label">
        Conditions <span class="segment-rule-builder__label-note">— match all</span>
      </span>

      <div v-for="(condition, index) in conditions" :key="index" class="segment-rule-builder__condition">
        <XlDropdown
          :data-testid="`condition-field-${index}`"
          :aria-label="`Condition ${index + 1} field`"
          :options="CONDITION_FIELDS"
          :model-value="condition.field"
          @update:model-value="(field) => onFieldChange(index, field)"
        />

        <XlDropdown
          :data-testid="`condition-op-${index}`"
          :aria-label="`Condition ${index + 1} operator`"
          :options="OPS_BY_FIELD[condition.field]"
          :model-value="condition.op"
          @update:model-value="(op) => onOpChange(index, op)"
        />

        <div class="segment-rule-builder__value">
          <XlDropdown
            v-if="condition.field === 'policy_type'"
            :data-testid="`condition-value-${index}`"
            :aria-label="`Condition ${index + 1} value`"
            :options="policyTypeOptions(condition.value)"
            :multiple="condition.op === 'within'"
            v-model="condition.value"
          />
          <XlField
            v-else-if="condition.field === 'state'"
            :name="`condition-value-${index}`"
            :data-testid="`condition-value-${index}`"
            :aria-label="`Condition ${index + 1} value`"
            :placeholder="condition.op === 'within' ? 'e.g. IL, IN, WI' : 'e.g. FL'"
            v-model="condition.value"
          />
          <XlField
            v-else-if="takesDays(condition.op)"
            :name="`condition-value-${index}`"
            :data-testid="`condition-value-${index}`"
            :aria-label="`Condition ${index + 1} value, in days`"
            placeholder="days, e.g. 90"
            :model-value="String(condition.value ?? '')"
            @update:model-value="(value) => onConditionNumberInput(index, value)"
          />
          <XlDateTimeField
            v-else
            v-model="condition.value"
            required
            :name="`condition-value-${index}`"
            :data-testid="`condition-value-${index}`"
            :aria-label="`Condition ${index + 1} value`"
          />

          <button
            type="button"
            class="segment-rule-builder__remove"
            :data-testid="`condition-remove-${index}`"
            :aria-label="`Remove condition ${index + 1}`"
            @click="removeCondition(index)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8" />
            </svg>
          </button>
        </div>
      </div>

      <XlButton variant="ghost" size="sm" data-testid="add-condition" @click="addCondition">
        <template #icon>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 6.2v11.6M6.2 12h11.6" />
          </svg>
        </template>
        Add condition
      </XlButton>

      <p v-if="shownError" class="segment-rule-builder__error" role="alert" data-testid="rule-validation-error">
        {{ shownError }}
      </p>
    </div>

    <!-- The counts themselves belong beside the form, in SegmentAudiencePanel;
         only the state of the lookup is the form's business. -->
    <div v-if="estimateLoading || estimateError" class="segment-rule-builder__estimate" data-testid="segment-estimate">
      <span v-if="estimateLoading">Estimating…</span>
      <span v-if="estimateError" class="segment-rule-builder__estimate-error" role="alert">{{ estimateError }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.segment-rule-builder {
  &__fld { margin-bottom: 16px; }

  &__label {
    display: block;
    margin-bottom: 6px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--xl-color-secondary-text);
  }

  &__label-note {
    font-weight: 400;
    color: var(--xl-color-outline-variant);
  }


  // One line, as the board draws it: field and value share what the 120px
  // operator and the × leave, and a long field name truncates.
  &__condition {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    // The field is never narrower than its name, as in the prototype; a row
    // with no room left for a whole date moves the value to a second line.
    > :nth-child(1) { flex: 1 1 0; min-width: max-content; }
    > :nth-child(2) { flex: 1 1 0; min-width: max-content; max-width: 120px; }

    :deep(.xl-dropdown .xl-dropdown__control) { min-width: 0; }
  }

  // The basis and minimums include the 8px gap and the 38px remove button, so
  // the value itself gets the field's share and a chosen policy type is never
  // cut.
  &__value {
    display: flex;
    flex: 1 1 46px;
    align-items: center;
    gap: 8px;
    min-width: 126px;

    > :first-child { flex: 1 1 0; min-width: 0; }
    > .xl-dropdown { min-width: max-content; }

    &:has(> .xl-dt) { min-width: 168px; }
    &:has(> .xl-dropdown) { min-width: min-content; }
  }

  &__remove {
    display: flex;
    flex: 0 0 38px;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    padding: 0;
    border: 1px solid var(--xl-color-border);
    border-radius: 11px;
    background: transparent;
    font: inherit;
    color: var(--xl-color-error);
    cursor: pointer;

    &:hover { background: var(--xl-color-background); }
  }

  &__error {
    margin: 8px 0 0;
    font-size: 12.5px;
    color: var(--xl-color-error);
  }

  &__estimate {
    margin: -8px 0 16px;
    font-size: 12.5px;
    color: var(--xl-color-outline);

    &-error { color: var(--xl-color-error); }
  }
}
</style>
